import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import api from '../../services/api';
import { Setting } from '../../types';
import './Dashboard.css';

// Site Dudes Logo
const siteDudesLogo = '/sd_logo_480px_136px.png';

interface DashboardHeaderProps {
  title?: string;
  showNav?: boolean;
  showEmailButton?: boolean;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ 
  title, 
  showNav = false,
  showEmailButton = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isUserAdmin } = useAuth();
  const { selectedClientId, setSelectedClientId, activeClients, hasMultipleClients } = useClient();

  const isClientUser = !isAdmin();
  const [clientLogo, setClientLogo] = useState<string | null>(null);

  const getStorageUrl = (path: string) => {
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '') || window.location.origin;
    return `${base}/storage/${path}`;
  };

  useEffect(() => {
    const fetchClientLogo = async () => {
      if (!isClientUser || !selectedClientId) {
        setClientLogo(null);
        return;
      }
      try {
        const response = await api.get<Setting[]>(`/settings?client_id=${selectedClientId}`);
        const logoSetting = response.data.find((s) => s.key === 'client_logo');
        const value = logoSetting?.value || null;
        if (value && !value.startsWith('data:') && !value.startsWith('http')) {
          setClientLogo(getStorageUrl(value));
        } else {
          setClientLogo(value);
        }
      } catch {
        setClientLogo(null);
      }
    };
    fetchClientLogo();
  }, [isClientUser, selectedClientId]);

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newClientId = parseInt(e.target.value, 10);
    setSelectedClientId(newClientId);
    // Navigate to dashboard to refresh data with new client
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
  };


  return (
    <>
      {/* Top Bar - White Background with Logo and Title */}
      <div className="top-bar">
        <div className="top-bar-content">
          <a href="/dashboard" className="logo-link" title="Home" onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}>
            <img src={siteDudesLogo} alt="Site Dudes Logo" className="site-dudes-logo" />
          </a>
          <span className="platform-name">Site Dudes Reviews</span>
        </div>
      </div>

      {/* Navigation Header - Orange Background */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="dashboard-title-section">
            <div className="client-logo-and-title">
              {clientLogo && isClientUser && (
                <img src={clientLogo} alt="Company Logo" className="client-logo-small" />
              )}
              <h1>{title || (isClientUser && activeClients.length > 0 ? activeClients.find(c => c.id === selectedClientId)?.client_name || activeClients[0]?.client_name : 'Dashboard')}</h1>
            </div>
            {isClientUser && hasMultipleClients && (
              <select
                value={selectedClientId || ''}
                onChange={handleClientChange}
                className="client-selector"
                title="Switch between companies"
              >
                {activeClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.client_name}
                  </option>
                ))}
              </select>
            )}
          </div>
          {showNav && (
            <div className="header-nav">
              <button
                className={`header-nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard')}
              >
                Reviews
              </button>
              {isUserAdmin() && (
                <button
                  className={`header-nav-link ${location.pathname.startsWith('/dashboard/users') ? 'active' : ''}`}
                  onClick={() => navigate('/dashboard/users')}
                >
                  Users
                </button>
              )}
              <button
                className={`header-nav-link ${location.pathname.startsWith('/dashboard/questions') ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/questions')}
              >
                Questions
              </button>
              <button
                className={`header-nav-link ${location.pathname.startsWith('/dashboard/settings') ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/settings')}
              >
                Settings
              </button>
              <button
                className={`header-nav-link ${location.pathname.startsWith('/dashboard/widget') ? 'active' : ''}`}
                onClick={() => navigate('/dashboard/widget')}
              >
                Widget
              </button>
            </div>
          )}
          <div className="header-actions">
            <span className="user-info">Welcome, {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''}</span>
            {showEmailButton && (
              <button onClick={() => navigate('/dashboard/email')} className="btn btn-primary" style={{ fontWeight: 'bold' }}>
                Send Review Email
              </button>
            )}
            <button onClick={handleLogout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default DashboardHeader;
